import './applySearchPlatePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import toUpper from 'lodash/toUpper';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const { plate, state, fetchVehicleBy, fetchVehicleFailedMessage } =
    useApplyStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onTouched',
    delayError: 200,
    defaultValues: {
      plateValue: plate,
      stateValue: state || '',
    },
  });

  const plateValidation = {
    required: t('validation.plate.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const isPageEnabled = useFeatureFlag(FeatureFlag.ENABLE_PLATE_TO_VIN_SERVICE);

  useEffect((): void => {
    // clear any fail message state upon rendering
    useApplyStore.setState({ fetchVehicleFailedMessage: null });

    if (!isPageEnabled) {
      navigate(previous());
    }
  }, [isPageEnabled]);

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { plateValue, stateValue } = values;

    if (plateValue && stateValue) {
      await fetchVehicleBy({ plate: toUpper(plateValue), state: stateValue });

      if (useApplyStore.getState().fetchVehicleSuccess) {
        navigate(next());
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page apply-search-plate-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applySearchPlate.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applySearchPlate.form.h1}>
                {t('ApplySearchPlatePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applySearchPlate.form.h2}>
                {t('ApplySearchPlatePage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Input
                    {...register('plateValue', plateValidation)}
                    data-testid={locator.pages.applySearchPlate.form.plateInput}
                    error={
                      errors.plateValue?.message ?? fetchVehicleFailedMessage
                    }
                    errorTestId={
                      locator.pages.applySearchPlate.form.plateInputError
                    }
                    label={t('ApplySearchPlatePage.plate')}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('stateValue', stateValidation)}
                    error={errors.stateValue}
                    errorTestId={
                      locator.pages.applySearchPlate.form.stateSelectError
                    }
                    inputTestId={
                      locator.pages.applySearchPlate.form.stateSelect
                    }
                    label={t('ApplySearchPlatePage.state')}
                    options={USStates.map((aState) => ({
                      name: aState.name,
                      value: aState.abbreviation,
                    }))}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={locator.pages.applySearchPlate.form.submitButton}
                  disabled={!isValid}
                  loader={isLoading}
                  loaderPosition="end"
                  fullWidth
                  submit
                >
                  {isLoading
                    ? t('ApplySearchPlatePage.searching')
                    : t('ApplySearchPlatePage.continue')}
                </Button>
              </div>
            </div>
            <footer data-testid={locator.pages.applySearchPlate.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplySearchPlatePage';
